import lodash from 'lodash';

export function toQuery(queryObj) {
    return objectToGraphQL(queryObj, 'query');
}

export function toMutation(queryObj) {
    return objectToGraphQL(queryObj, 'mutation');
}

export function sanitizeInput(v) {

    const sanitizedInput = v.replace(/\\/g,'\\\\')
        .replace(/\\{0}"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');

    return sanitizedInput;
}

export function node(name, fields) {
    return {
        name: name,
        fields: fields
    };
}

export function aliasNode(alias, name, fields) {
    return {
        alias: alias,
        name: name,
        fields: fields
    };
}

export function nodeWithArgs(name, args, fields) {
    return {
        name: name,
        args: args,
        fields: lodash.isEmpty(fields) ? undefined : fields
    };
}

export function aliasNodeWithArgs(alias, name, args, fields) {
    return {
        alias: alias,
        name: name,
        args: args,
        fields: lodash.isEmpty(fields) ? undefined : fields
    };
}

export function enumeration(string) {
    return { enum: string };
}

export function inlineFragment(fragmentName, fields) {
    return {
        fragmentName,
        fields
    };
}

export function quoteInputValue(value) {
    if (isEnumeration(value)) {
        return value.enum;
    }
    if (typeof(value) === 'number') {
        return value.toString();
    }
    if (typeof(value) === 'string') {
        return `"${sanitizeInput(value)}"`;
    }
    if (typeof(value) === 'function') {
        return quoteInputValue(value());
    }
    return value;
}

export function transformIntoEnumerations(object, enumKeys) {
    if (!enumKeys || enumKeys.length === 0) return object;
    const newObject = {};
    for (const key of Object.keys(object)) {
        newObject[key] = enumKeys.indexOf(key) >= 0
            ? enumeration(object[key])
            : object[key];
    }
    return newObject;
}

export class GraphQLQueryWriter {
    constructor(start) {
        this.openLevels = [];
        this._string = start;
    }

    append(string) {
        this._string += string;
    }

    writeSpace() {
        this.append(' ');
    }

    writeComma() {
        this.append(',');
    }

    writeColon() {
        this.append(':');
    }

    writeName(name) {
        this.append(name);
    }

    writeValue(value) {
        this.append(quoteInputValue(value));
    }

    writeArguments(args) {
        if (!lodash.isPlainObject(args)) {
            throw Error('args must be a plain object or array of plain objects.');
        }
        let commasLeft = Object.keys(args).length - 1;
        for (const arg in args) {
            const value = args[arg];
            if (!Object.prototype.hasOwnProperty.call(args, arg) || isNullValue(value)) continue;
            this.writeName(arg);
            this.writeColon();
            this.writeSpace();

            if (lodash.isPlainObject(value) && !isEnumeration(value)) {
                this.writeArgumentObject(value);
            } else if (lodash.isArray(value)) {
                this.writeArgumentObjectArray(value);
            } else {
                this.writeValue(value);
            }
            if (commasLeft > 0) {
                this.writeComma();
                this.writeSpace();
                commasLeft -= 1;
            }
        }
    }

    writeArgumentObject(args) {
        this.startBlock(false);
        this.writeArguments(args);
        this.endBlock();
    }

    writeArgumentObjectArray(args) {
        this.startBrace();
        let commasLeft = args.length - 1;
        for (const arg of args) {
            if (lodash.isPlainObject(arg)) {
                this.writeArgumentObject(arg);
            } else {
                this.writeValue(arg);
            }
            if (commasLeft > 0) {
                this.writeComma();
                commasLeft -= 1;
            }
        }
        this.endBrace();
    }

    writeFields(fields) {
        if (!lodash.isArray(fields)) {
            throw new Error('fields must be an array');
        }
        let commasLeft = fields.length - 1;
        for (const field of lodash.compact(fields)) {
            this.writeField(field);
            if (commasLeft > 0) {
                this.writeComma();
                this.writeSpace();
                commasLeft -= 1;
            }
        }
    }

    writeField(field) {
        if (isInlineFragment(field)) {
            this.writeInlineFragment(field);
        } else if (lodash.isPlainObject(field)) {
            this.writeQuery(field);
        } else {
            this.writeName(field);
        }
    }

    writeQuery(query) {
        if (lodash.isArray(query)) {
            lodash.compact(query).forEach(q => this.writeQuery(q));
        } else {
            if (query.alias) {
                this.writeName(query.alias);
                this.writeColon();
                this.writeSpace();
            }
            this.writeName(query.name);
            const hasArgs = lodash.some(query.args, arg => !isNullValue(arg));
            const hasFields = lodash.some(query.fields, arg => !isNullValue(arg));
            if (hasArgs) {
                this.startParenthesis();
                this.writeArguments(query.args);
                this.endParenthesis();
            }
            if (hasFields) {
                this.startBlock();
                this.writeFields(query.fields);
                this.endBlock();
            }
            if (!hasArgs && !hasFields) {
                this.writeSpace();
            }
        }
    }

    writeInlineFragment(inlineFragment) {
        const { fragmentName, fields } = inlineFragment;
        this.append(`... on ${fragmentName}`);
        this.startBlock();
        this.writeFields(fields);
        this.endBlock();
    }

    startParenthesis() {
        this.append('(');
        this.openLevels.push('(');
    }

    endParenthesis() {
        this.closeOpenLevels(')');
    }

    startBrace() {
        this.append('[');
        this.openLevels.push('[');
    }

    endBrace() {
        this.closeOpenLevels(']');
    }

    startBlock(space = true) {
        if (space) {
            this.writeSpace();
        }
        this.append('{');
        this.openLevels.push('{');
    }

    endBlock() {
        this.closeOpenLevels('}');
        this.writeSpace();
    }

    closeOpenLevels(untilToken) {
        let closingToken = null;
        while (this.openLevels.length > 0) {
            closingToken = ENDING_BLOCKS[this.openLevels.pop()];
            this.append(closingToken);
            if (closingToken === untilToken) {
                return;
            }
        }
    }

    toString() {
        this.closeOpenLevels();
        return this._string;
    }
}

function isEnumeration(object) {
    return lodash.isPlainObject(object) && typeof(object.enum) === 'string';
}

function isInlineFragment(object) {
    return lodash.isPlainObject(object) && typeof(object.fragmentName) === 'string';
}

function isNullValue(value) {
    return typeof value === 'undefined' || value === null;
}

/**
 *      [{
 *          name: 'addDescriptor',
 *          args: {id: 1, descriptor: {name: 'test', displayName: 'jim'}},
 *          fields: [
 *              'id',
 *              'displayName'
 *              {
 *                  name: 'children',
 *                  args: ''
 *              }
 *          ]
 *      }]
 */
function objectToGraphQL(queryObj, rootName) {
    const writer = new GraphQLQueryWriter(rootName);
    writer.startBlock();
    writer.writeQuery(queryObj);
    writer.endBlock();
    return writer.toString();
}

const ENDING_BLOCKS = {
    '{': '}',
    '(': ')',
    '[': ']'
};